import { Fragment, useMemo, useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@kizen/kds/Button';
import produce from 'immer';
import Draggable from 'react-draggable';
import ActivityService from 'services/ActivityService';
import { camelToSnakeCaseKeys } from 'services/helpers';
import Select from 'components/Inputs/Select';
import CustomFieldChooserModal from 'components/Modals/CustomFieldChooser';
import ActivityBuilderPreview from 'components/Modals/ActivityBuilderPreview';
import useModal from 'components/Modals/useModal';

import BuilderField, {
  fieldMargin,
  menuBuildOptions as menuBuildOptionsOrigin,
} from 'components/Builders/CustomFieldsBuilder/Field';
import { FieldPlaceholder } from 'components/Builders/CustomFieldsBuilder/styles';

import {
  HorizontalDropzone,
  VerticalDropzone,
} from 'components/DragAndDropLayout/Dropzone';

import { KizenTypography } from 'app/typography';
import {
  dropzoneIs,
  applyDropzone,
} from 'components/DragAndDropLayout/helpers';
import { isOwnerField } from 'checks/fields';

import { getFieldDropzone } from 'components/Builders/CustomFieldsBuilder/helpers';

import {
  MENU_ACTION_SHOW,
  MENU_ACTION_HIDE,
  MENU_ACTION_REQUIRED,
  MENU_ACTION_OPTIONAL,
  MENU_ACTION_EDIT_OPTIONALS,
  MENU_ACTION_ADD_RULE,
} from 'components/Builders/CustomFieldsBuilder/menuActionOptions';

import { isDefaultUndeletableNotesField } from 'checks/fields';
import { invalidate } from 'queries/invalidate';
import BuilderActivity from './Activity';
import ActivityFieldWizard from './Field';

import {
  BigCard,
  TitleWrapper,
  ActivitiesTitle,
  ActivitiesFooter,
  FooterNewFieldTypography,
  FieldLayout,
  ItemWrapper,
  RightHeader,
  ObjectAssociationsContainer,
  ObjectAssociationsSelectorRow,
  ObjectAssociationsSwitch,
} from './styles';
import { useCustomFieldChooserWithCache } from 'components/Modals/CustomFieldChooser/hooks';
import useExportFieldMetadata from 'hooks/useExportFieldMetadata';
import { OBJECT_ASSOCATIONS } from 'components/GenericWizard/wizards/scheduleActivity';
import {
  deleteLayoutItemFromVisibilityRules,
  isVisibilityRuleInvalid,
} from 'ts-activities/utils';
import { getOriginalError } from 'services/AxiosService';
import { toastVariant, useToast } from 'components/ToastProvider';
import { flushSync } from 'react-dom';
import UUID from 'utility/UUID';
import { AssociationBuilder } from './AssociationBuilder';
import { TextBlockModal } from './TextBlockModal';

// we do not show required options for activity Owner field
class menuBuildOptions extends menuBuildOptionsOrigin {
  static required = (field, t) => {
    if (isOwnerField(field)) return [];
    return menuBuildOptionsOrigin.required(field, t);
  };
}

function NewField({ onClick, label, disabled, ...rest }) {
  return (
    <FooterNewFieldTypography
      as="span"
      weight="bold"
      size="buttonLabel"
      textTransform="uppercase"
      onClick={(ev) => {
        if (!disabled) {
          onClick(ev);
        }
      }}
      disabled={disabled}
      {...rest}
    >
      {label}
    </FooterNewFieldTypography>
  );
}

const ACTIVITY_CUSTOM_FIELD = 'activity_custom_field';

const getMenuOptionsForLayoutItem = (item, t) => {
  if (item.type === 'section') {
    return [
      { value: 'edit-text-block', label: t('Edit') },
      ...menuBuildOptions.delete({ isDeletable: true }, t),
    ];
  }

  if (isDefaultUndeletableNotesField(item.field)) {
    return menuBuildOptions.visibility(item.field, t, {
      showLabel: t('Activate'),
      hideLabel: t('Deactivate'),
    });
  }

  return [
    ...menuBuildOptions.edit({ ...item.field, isDefault: false }, t),
    ...menuBuildOptions.visibility(item.field, t, {
      showLabel: t('Activate'),
      hideLabel: t('Deactivate'),
    }),
    ...(!item.field.meta.isReadonly
      ? menuBuildOptions.required(item.field, t)
      : []),
    ...menuBuildOptions.addRule(item.field, t),
    ...menuBuildOptions.delete({ ...item.field, isDefault: false }, t),
  ].filter(Boolean);
};

const findFieldInActivity = ({ fields }, fieldId) => {
  return fields.find((field) => field.id === fieldId);
};

const moveFieldToBottom = (layout, fieldId) => {
  const result = [];
  let last = null;

  for (const item of layout) {
    if (item.fieldId === fieldId) {
      last = item;
    } else {
      result.push(item);
    }
  }

  return last ? result.concat(last) : result;
};

const getLayoutForAPI = (layout) => {
  return layout.map((item) => {
    const { id, ...data } = item;

    if ('internalName' in data) {
      const { internalName, ...rest } = data;
      return { ...rest, internal_name: internalName };
    }

    return data;
  });
};

const getInternalNameValidator = (layout, t) => {
  return (internalName, index) => {
    for (const [idx, item] of layout.entries()) {
      if (item.type === 'field' && item.fieldId === internalName) {
        // unlikely, but since layout items don't have an id we want to
        // guarantee items are unique across 'field_id' and 'internal_name'
        return t('Field identifiers cannot be used as internal names');
      }
      if (index === idx) {
        continue;
      }
      if (item.type === 'section' && item.internalName === internalName) {
        return t('Internal name already exists');
      }
    }

    return null;
  };
};

// dynamic tags expect a model and a fetch url, I've asked Scott where the tags should go,
// but this stops things crashing for now
const activityToModel = (activity) => {
  const { fields, name, ...others } = activity;
  return {
    ...others,
    name,
    description: 'Submissions',
    fetchUrl: '',
    noLink: true,
    hideCount: true,
  };
};

/**
 * @typedef {import('../../types').ActivityState} ActivityState
 * @typedef {{
 *  activity: ActivityState;
 *  textBlockInternalNames: Record<string, string>;
 *  setActivity: React.Dispatch<React.SetStateAction<ActivityState>>;
 *  setTextBlockInternalNames: React.Dispatch<React.SetStateAction<Record<string, string>>>;
 * }} Props
 * @param {Props} props
 */
function ActivitiesBuilder({
  activity,
  setActivity,
  textBlockInternalNames,
  setTextBlockInternalNames,
}) {
  const { t } = useTranslation();
  const history = useHistory();
  const queryClient = useQueryClient();
  const [fieldDragging, setFieldDragging] = useState(null);
  const [fieldDropzone, setFieldDropzone] = useState(null);
  const [showToast] = useToast();
  const [wizardData, setWizardData] = useState({});
  const [stagedDisplayNamesById, setStagedDisplayNamesById] = useState({});
  const [showCustomFieldModal, setShowCustomFieldModal] = useState(false);
  const [textBlockModal, setTextBlockModal] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const { handleFieldExport } = useExportFieldMetadata({ activity });

  const { associationMode, isAssociationRequired } = activity;
  const showAssociationRequiredToggle =
    associationMode === OBJECT_ASSOCATIONS.ALL_OBJECTS_ASSOCIATED ||
    associationMode === OBJECT_ASSOCATIONS.SELECTED_OBJECTS_ASSOCIATED;

  useEffect(() => {
    if (fieldDragging) {
      document.body.style.cursor = 'grabbing';
    } else {
      document.body.style.cursor = 'default';
    }
  }, [fieldDragging]);

  const customActivityFields = useMemo(() => {
    return (
      activity.fields?.filter(
        ({ fieldType }) => fieldType === ACTIVITY_CUSTOM_FIELD
      ) ?? []
    );
  }, [activity]);

  const needsInitialization =
    customActivityFields.length > 0 &&
    Object.keys(stagedDisplayNamesById).length === 0;

  useEffect(() => {
    if (needsInitialization) {
      const intialDisplayNamesById = customActivityFields.reduce(
        (acc, field) => {
          acc[field?.id] = field.displayName;
          return acc;
        },
        {}
      );
      setStagedDisplayNamesById(intialDisplayNamesById);
    }
  }, [needsInitialization, customActivityFields]);

  const customFieldChooserProps = useCustomFieldChooserWithCache(
    (categories, field) => {
      return categories.reduce((acc, category) => {
        const options = category.options.filter(
          ({ item: { isReadOnly, id } }) => {
            return (
              !isReadOnly &&
              (field ||
                !activity.fields?.some(
                  ({ customObjectField }) => customObjectField?.id === id
                ))
            );
          }
        );

        if (!options.length) {
          return acc;
        }

        acc.push({ ...category, options });

        return acc;
      }, []);
    }
  );
  const applyFieldDropzone = async () => {
    if (!fieldDragging || !fieldDropzone) {
      return;
    }
    const nextActivity = produce(activity, (draft) => {
      const [fromFields] = applyDropzone(
        fieldDragging,
        [draft.layout],
        fieldDropzone
      );
      draft.layout = fromFields;
    });

    // Optimistically display the drop
    setActivity(nextActivity);
    await ActivityService.v2UpdateActivity({
      id: nextActivity.id,
      layout: getLayoutForAPI(nextActivity.layout),
    });
  };

  const handleDeleteSection = async (id) => {
    let deletedSection = null;

    const nextActivity = produce(activity, (draft) => {
      const index = draft.layout.findIndex((x) => x.id === id);
      deletedSection = { ...draft.layout[index] };
      draft.layout.splice(index, 1);
    });

    setActivity(nextActivity);

    await ActivityService.v2UpdateActivity({
      id: activity.id,
      layout: getLayoutForAPI(nextActivity.layout),
    });

    if (deletedSection) {
      updateVisibilityRulesAfterDeleteOrDeactivate({
        internal_name: deletedSection.internalName,
      });
    }
  };

  const updateSection = async (id, { label, internalName }) => {
    let visibilityRulesUpdated = false;

    const nextActivity = produce(activity, (draft) => {
      const item = draft.layout.find((x) => x.id === id);

      if (item) {
        if (label) {
          item.label = label;
        }

        if (internalName) {
          if (Array.isArray(draft.visibilityRules)) {
            for (const rule of draft.visibilityRules) {
              for (const layoutItem of rule.layoutItems) {
                if (layoutItem.internalName === item.internalName) {
                  layoutItem.internalName = internalName;
                  visibilityRulesUpdated = true;
                }
              }
            }
          }

          item.internalName = internalName;
        }
      }
    });

    setActivity(nextActivity);

    const payload = {
      id: activity.id,
      layout: getLayoutForAPI(nextActivity.layout),
    };

    if (visibilityRulesUpdated) {
      payload.visibilityRules = nextActivity.visibilityRules;
    }

    await ActivityService.v2UpdateActivity(payload);
  };

  const updateFieldStore = useCallback(
    async (field) => {
      const { id } = field;
      const nextActivity = produce(activity, (draft) => {
        const f = findFieldInActivity(draft, id);

        if (f) {
          f.description = field.description;
          f.descriptionVisibility = field.descriptionVisibility;
          f.name = field.name;
          f.isRequired = field.isRequired;
          f.displayName = field.displayName;
          f.options = field.options;
          f.meta = field.meta;

          if (f.fieldType === 'relationship') {
            f.relation = field.relation;
          }
        } else {
          draft.fields.push(field);
          draft.layout.push({ columns: 1, id, fieldId: id, type: 'field' });
        }
      });

      setActivity(nextActivity, nextActivity.fields);

      await ActivityService.v2UpdateActivity({
        id: nextActivity.id,
        layout: getLayoutForAPI(nextActivity.layout),
      });
    },
    [activity, setActivity]
  );

  const [activityFieldWizardModalProps, , activityFieldWizardModal] = useModal({
    handleSubmit: (field) => {
      updateFieldStore(field);

      if (field.fieldType === 'relationship') {
        queryClient.invalidateQueries({
          queryKey: [
            'related_objects',
            field.relation.relatedObject,
            field.id,
            { filter_activity_field_id: field.id },
          ],
        });
      }
    },
  });

  const handleFieldEditOptions = async (field) => {
    setWizardData({
      activityObjectId: activity.id,
      field,
      model: activityToModel(activity), // the activity is the model in this case as far as the wizard is concerned
    });
    activityFieldWizardModal.show();
  };

  const handleNewActivityField = () => {
    setWizardData({
      activityObjectId: activity.id,
      model: activityToModel(activity), // the activity is the model in this case as far as the wizard id concerned
    });

    activityFieldWizardModal.show();
  };

  const updateVisibilityRulesAfterDeleteOrDeactivate = async (
    item,
    isDelete,
    nextActivity
  ) => {
    const updatedVisibilityRules = Array.isArray(activity.visibilityRules)
      ? deleteLayoutItemFromVisibilityRules(
          camelToSnakeCaseKeys(activity.visibilityRules),
          item
        )
      : [];
    const invalidChecks = updatedVisibilityRules.map(isVisibilityRuleInvalid);
    const firstInvalidRule = invalidChecks.findIndex((x) => x === true);

    if (firstInvalidRule > -1) {
      const error_message = isDelete
        ? t('The field has been deleted')
        : t('The field has been deactivated');
      const invalidCount = invalidChecks.filter((x) => x === true).length;
      const withErrorMessages = updatedVisibilityRules.map((vr) => {
        if (vr.rule.filters.length) return vr;
        const filter = {
          view_model: [['field_id', { error: true, error_message }]],
        };
        return { ...vr, rule: { ...vr.rule, filters: [filter] } };
      });
      history.push(`/activities/${activity.id}/rules`, {
        visibilityRules: withErrorMessages,
        scrollCardIndex: firstInvalidRule,
        errorMessage: t(
          'You have {{count}} invalid {{rules}} after {{action}} your {{item}}. Please update your rules before leaving activity settings.',
          {
            count: invalidCount,
            rules: invalidCount > 1 ? t('rules') : t('rule'),
            action: isDelete ? t('deleting') : t('deactivating'),
            item: 'internal_name' in item ? t('text block') : t('field'),
          }
        ),
      });
    } else {
      const res = await ActivityService.v2UpdateActivity({
        id: activity.id,
        visibilityRules: updatedVisibilityRules,
      });
      invalidate.ACTIVITIES.ACTIVITY(activity.id);

      if (nextActivity) {
        setActivity({ ...nextActivity, visibilityRules: res.visibilityRules });
      } else {
        setActivity((prev) => ({
          ...prev,
          visibilityRules: res.visibilityRules,
        }));
      }
    }
  };

  const handleFieldDelete = async ({ id }) => {
    const nextActivity = produce(activity, (draft) => {
      const f = draft.fields.filter((field) => field.id !== id);
      if (f) {
        draft.fields = f;
        draft.layout = draft.layout.filter(
          (x) => x.type === 'section' || x.fieldId !== id
        );
      }
    });
    setActivity(nextActivity);

    await ActivityService.v2DeleteField({
      activityObjectId: nextActivity.id,
      id,
    });

    if (activity.visibilityRules?.length) {
      updateVisibilityRulesAfterDeleteOrDeactivate({ field_id: id }, true);
    }
  };

  const handleResize = async (cols, { id }) => {
    const nextActivity = produce(activity, (draft) => {
      const item = draft.layout.find((x) => x.id === id);
      item.columns = cols;
    });
    setActivity(nextActivity);

    await ActivityService.v2UpdateActivity({
      id: nextActivity.id,
      layout: getLayoutForAPI(nextActivity.layout),
    });
  };
  const handleNewCustomField = () => {
    setWizardData({
      title: t('Add Custom Field'),
      displayNamePrefix: 'Activity',
      activityObjectId: activity.id,
      activityCustomObjects: activity.customObjects,
    });
    setShowCustomFieldModal(true);
    return true;
  };

  const handleEditCustomField = (field) => {
    setWizardData({
      title: t('Edit Custom Field'),
      displayNamePrefix: 'Activity',
      field,
      activityObjectId: activity.id,
    });
    setShowCustomFieldModal(true);
    return true;
  };

  const handleFieldSelectAction = async ({ value: action }, field) => {
    if (
      action === MENU_ACTION_SHOW ||
      action === MENU_ACTION_HIDE ||
      action === MENU_ACTION_REQUIRED ||
      action === MENU_ACTION_OPTIONAL
    ) {
      const { id } = field;
      let updateFields = false;
      let updateActivityLayout = false;

      const nextActivity = produce(activity, (draft) => {
        const f = findFieldInActivity(draft, id);
        if (f) {
          switch (action) {
            case MENU_ACTION_HIDE:
              f.isHidden = true;
              f.isRequired = false;
              updateFields = true;
              updateActivityLayout = true;
              draft.layout = moveFieldToBottom(draft.layout, id);
              break;

            case MENU_ACTION_SHOW:
              f.isHidden = false;
              break;

            case MENU_ACTION_REQUIRED:
              f.isRequired = true;
              break;

            case MENU_ACTION_OPTIONAL:
              f.isRequired = false;
              break;

            default:
              break;
          }
        }
      });

      setActivity(nextActivity, nextActivity.fields);

      const f = findFieldInActivity(nextActivity, id);
      if (f) {
        const { isHidden, isRequired } = f;
        const { id: activityObjectId } = nextActivity;
        const payload = {
          id,
          activityObjectId,
          isHidden,
          isRequired,
        };

        const promises = [ActivityService.v2UpdateField(payload)];

        if (updateActivityLayout) {
          promises.push(
            ActivityService.v2UpdateActivity({
              id: activity.id,
              layout: getLayoutForAPI(nextActivity.layout),
            })
          );
        }

        await Promise.all(promises);

        if (updateFields && activity.visibilityRules?.length) {
          updateVisibilityRulesAfterDeleteOrDeactivate(
            { field_id: id },
            false,
            nextActivity
          );
        }
      }
    } else if (action === MENU_ACTION_EDIT_OPTIONALS) {
      if (field.fieldType === ACTIVITY_CUSTOM_FIELD) {
        // custom field
        handleEditCustomField(field);
      } else {
        // activity field
        handleFieldEditOptions(field);
      }
    } else if (action === MENU_ACTION_ADD_RULE) {
      history.push(`/activities/${activity.id}/rules`, {
        addRuleField: camelToSnakeCaseKeys(field),
      });
    } else if (action === 'edit-text-block') {
      setTextBlockModal({
        ...field,
        index: activity.layout.findIndex((item) => item.id === field.id),
      });
    }
  };

  const handleChangeFieldDescription = async (
    displayName,
    { id, displayName: prev }
  ) => {
    if (displayName === prev) {
      return;
    }

    if (displayName === '') {
      // throw an error so the stagedDescription is reset
      throw new Error(`Field name can't be blank`);
    }

    const { id: activityObjectId } = activity;
    const payload = {
      activityObjectId,
      id,
      displayName,
    };

    const nextActivity = produce(activity, (draft) => {
      const f = findFieldInActivity(draft, id);
      if (f) {
        f.displayName = displayName;
      }
    });
    setActivity(nextActivity, nextActivity.fields);

    await ActivityService.v2UpdateField(payload);
  };

  const handleNewCustomFieldSave = useCallback(
    async (field) => {
      // todo back out adding fieldType to meta once api returns model_field
      const { id: activityObjectId } = activity;
      try {
        let payload;
        const { orgField } = field;
        if (!orgField) {
          const {
            id: customObjectField,
            isRequired,
            isDefault,
            displayName,
            description,
            descriptionVisibility,
            meta,
          } = field;
          payload = {
            activityObjectId,
            fieldType: ACTIVITY_CUSTOM_FIELD,
            isDefault,
            description,
            descriptionVisibility,
            displayName,
            customObjectField,
            isRequired,
            meta: { ...meta, cols: 1 },
          };
          payload = await ActivityService.v2CreateField(payload);
        } else {
          const {
            isRequired,
            displayName,
            meta,
            name,
            description,
            descriptionVisibility,
          } = field;
          const { id } = orgField;
          payload = {
            activityObjectId,
            id,
            displayName,
            description,
            descriptionVisibility,
            isRequired,
            meta,
            name,
          };
          payload = await ActivityService.v2UpdateField(payload);
        }
        updateFieldStore(payload);
        return { success: true, error: null };
      } catch (error) {
        const orig = getOriginalError(error);
        if (orig?.message) {
          showToast({
            variant: toastVariant.FAILURE,
            message: orig.message,
          });
          setShowCustomFieldModal(true);
        }
        return { success: false, error: orig };
      }
    },
    [activity, showToast, updateFieldStore]
  );

  const ruleIndexesForLayoutItem = useMemo(() => {
    const init = { hidden: {}, conditions: {} };

    if (!activity.visibilityRules) {
      return init;
    }

    return activity.visibilityRules.reduce((acc, vr, idx) => {
      for (const item of vr.layoutItems) {
        const id = item.fieldId || item.internalName;

        if (acc.hidden[id] === undefined) {
          acc.hidden[id] = idx;
        }
      }
      for (const cond of vr.rule.filters) {
        if (acc.conditions[cond.activityFieldId] === undefined) {
          acc.conditions[cond.activityFieldId] = idx;
        }
      }
      return acc;
    }, init);
  }, [activity.visibilityRules]);

  const handleCustomFieldSave = useCallback(
    async (field, originalId) => {
      const response = handleNewCustomFieldSave(field); // returns promise that resolves to { success: boolean, error: any }
      const id = originalId || field.id;
      setStagedDisplayNamesById((prev) => {
        return {
          ...prev,
          [id]: field.displayName,
        };
      });
      return response;
    },
    [handleNewCustomFieldSave]
  );

  const layoutData = useMemo(() => {
    const visible = [];
    const hidden = [];
    const fieldLookup = activity.fields.reduce((acc, field) => {
      acc[field.id] = field;
      return acc;
    }, {});

    for (const item of activity.layout) {
      if (item.type === 'field' && !fieldLookup[item.fieldId]) {
        continue;
      }

      if (item.type === 'field' && fieldLookup[item.fieldId].isHidden) {
        hidden.push({ ...item, field: fieldLookup[item.fieldId] });
      } else {
        if (item.type === 'field') {
          visible.push({ ...item, field: fieldLookup[item.fieldId] });
        } else {
          visible.push(item);
        }
      }
    }

    return visible.concat(hidden);
  }, [activity]);

  const validateInternalName = (index) => (internalName) => {
    const validator = getInternalNameValidator(activity.layout, t);
    return validator(internalName, index);
  };

  const onHideTextBlockModal = () => {
    setTextBlockModal(null);
  };

  const onConfirmTextBlockModal = async ({ internalName, label }) => {
    if (textBlockModal.id) {
      await updateSection(textBlockModal.id, {
        label,
        internalName,
      });

      setTextBlockInternalNames((prev) => ({
        ...prev,
        [textBlockModal.id]: internalName,
      }));
    } else {
      const section = {
        columns: 2,
        type: 'section',
        label,
        internalName,
        id: UUID.generate(),
      };
      const nextActivity = produce(activity, (draft) => {
        draft.layout.push(section);
      });

      await ActivityService.v2UpdateActivity({
        id: activity.id,
        layout: getLayoutForAPI(nextActivity.layout),
      });

      setActivity(nextActivity);

      setTextBlockInternalNames((prev) => {
        return { ...prev, [section.id]: internalName };
      });
    }

    setTextBlockModal(null);
  };

  let x = 0;
  let endOfLine = true;

  const handleChangeObjectAssociationMode = async (option) => {
    const associationMode = option.value;
    const isAssociationRequired =
      associationMode !== OBJECT_ASSOCATIONS.NO_OBJECT_ASSOCIATIONS;

    setActivity((prev) => {
      return {
        ...prev,
        associationMode,
        associatedObjects: [],
        isAssociationRequired,
      };
    });

    try {
      const payload = { id: activity.id, isAssociationRequired };

      if (associationMode !== OBJECT_ASSOCATIONS.SELECTED_OBJECTS_ASSOCIATED) {
        payload.associationMode = associationMode;
        payload.associatedObjects = null;
      }

      await ActivityService.v2UpdateActivity(payload);
    } catch (error) {
      const orig = getOriginalError(error);

      if (orig?.message) {
        showToast({
          message: orig?.message,
          variant: toastVariant.FAILURE,
        });
      }
    }
  };

  return (
    <>
      <BigCard data-qa="activity-builder-modal">
        <ObjectAssociationsContainer>
          <ActivitiesTitle as={TitleWrapper}>
            <KizenTypography as="h3" type="subheader">
              {t('Object Associations')}
            </KizenTypography>
          </ActivitiesTitle>

          <ObjectAssociationsSelectorRow>
            <Select
              label={t('Activity Associations Settings')}
              placeholder={t('Select Objects')}
              value={associationMode}
              onChange={handleChangeObjectAssociationMode}
              options={[
                {
                  label: t('Select Objects'),
                  value: OBJECT_ASSOCATIONS.SELECTED_OBJECTS_ASSOCIATED,
                },
                {
                  label: t('All Objects with Activities Enabled'),
                  value: OBJECT_ASSOCATIONS.ALL_OBJECTS_ASSOCIATED,
                },
                {
                  label: t('No Object Associations'),
                  value: OBJECT_ASSOCATIONS.NO_OBJECT_ASSOCIATIONS,
                },
              ]}
            />
            {showAssociationRequiredToggle && (
              <ObjectAssociationsSwitch
                removeMargin
                checked={isAssociationRequired}
                label={t('At Least 1 Association Required')}
                tooltipPlacement="right"
                tooltip={t(
                  'This will be enforced on Scheduled Activities whether scheduled by users or agentic workflows. It will also be enforced when logging Activities.'
                )}
                onChange={async () => {
                  setActivity((prev) => {
                    return {
                      ...prev,
                      isAssociationRequired: !isAssociationRequired,
                    };
                  });

                  await ActivityService.v2UpdateActivity({
                    id: activity.id,
                    isAssociationRequired: !isAssociationRequired,
                  });
                }}
              />
            )}
          </ObjectAssociationsSelectorRow>

          {associationMode ===
            OBJECT_ASSOCATIONS.SELECTED_OBJECTS_ASSOCIATED && (
            <AssociationBuilder activity={activity} setActivity={setActivity} />
          )}
        </ObjectAssociationsContainer>

        <ActivitiesTitle as={TitleWrapper}>
          <KizenTypography as="h3" type="subheader">
            {t('Activity Fields')}
          </KizenTypography>
          <RightHeader>
            <Button
              className="pb-[10px]"
              color="secondary"
              onClick={handleFieldExport}
              variant="text"
            >
              {t('Export Field Metadata')}
            </Button>
          </RightHeader>
        </ActivitiesTitle>

        <BuilderActivity
          onMouseMove={(ev) => {
            if (fieldDragging) {
              setFieldDropzone(
                getFieldDropzone(
                  fieldDragging,
                  { id: activity.id, fields: activity.layout },
                  ev
                )
              );
            }
          }}
        >
          <FieldLayout>
            <>
              {dropzoneIs(fieldDropzone, {
                sectionId: activity.id,
                position: 'first',
              }) && <HorizontalDropzone margin={fieldMargin} />}
            </>
            {layoutData.map((item, i, arr) => {
              const isTopDropZone = dropzoneIs(fieldDropzone, {
                id: item.id,
                position: 'before',
                direction: 'horizontal',
              });
              const isLeftDropZone = dropzoneIs(fieldDropzone, {
                id: item.id,
                position: 'before',
                direction: 'vertical',
              });
              const isRightDropZone = dropzoneIs(fieldDropzone, {
                id: item.id,
                position: 'after',
                direction: 'vertical',
              });
              const isBottomDropZone = dropzoneIs(fieldDropzone, {
                id: item.id,
                position: 'after',
                direction: 'horizontal',
              });
              const wasEndOfLine = endOfLine;
              const cols = item.columns;
              const nextlayoutItem = arr[i + 1];
              const nextCols = nextlayoutItem ? nextlayoutItem.columns : 0;
              endOfLine = x + cols >= 2 || x + cols + nextCols > 2;
              x = endOfLine ? 0 : 1;

              if (item.type === 'field') {
                const isNotesField = isDefaultUndeletableNotesField(item.field);
                const menuOptions = getMenuOptionsForLayoutItem(item, t);
                const f = { ...item.field };
                if (f && isOwnerField(f)) {
                  f.isRequired = true;
                }

                return (
                  <Fragment key={item.id}>
                    {wasEndOfLine && isTopDropZone && (
                      <HorizontalDropzone margin={fieldMargin} />
                    )}
                    <ItemWrapper
                      data-qa="builder-field"
                      key={f.id}
                      className="FieldItemWrapper"
                      cols={2}
                      width={cols}
                      dragging={fieldDragging && fieldDragging.id === f.id}
                    >
                      {isLeftDropZone && (
                        <VerticalDropzone margin={fieldMargin} />
                      )}
                      <FieldPlaceholder />
                      <Draggable
                        disabled={f.isHidden || !activity.id}
                        onStart={() => flushSync(() => setFieldDragging(item))}
                        onStop={() => {
                          flushSync(() => {
                            applyFieldDropzone();
                            setFieldDragging(null);
                            setFieldDropzone(null);
                          });
                        }}
                        position={fieldDragging ? null : { x: 0, y: 0 }}
                        handle=".FieldHandle"
                      >
                        <BuilderField
                          field={f}
                          columns={item.columns}
                          handleProps={{ className: 'FieldHandle' }}
                          dragging={fieldDragging && fieldDragging.id === f.id}
                          onChangeDescription={handleChangeFieldDescription}
                          onConfirmDelete={handleFieldDelete}
                          onClickChangeColumnWidth={handleResize}
                          menuOptions={menuOptions}
                          isReadonly={item.field.meta.isReadonly}
                          isDisabled={isNotesField || !activity.id}
                          onLabelChange={(id, updatedLabel) =>
                            setStagedDisplayNamesById((prev) => {
                              return {
                                ...prev,
                                [id]: updatedLabel,
                              };
                            })
                          }
                          label={
                            stagedDisplayNamesById[f.id] !== undefined
                              ? stagedDisplayNamesById[f.id]
                              : f.displayName
                          }
                          onSelectAction={handleFieldSelectAction}
                          disableChangeColumnWidth={isNotesField}
                          confirmDeleteMessage={t(
                            'This will permanently delete this field and all historical submission data from this activity.'
                          )}
                          canDeleteFields
                          isHiddenByRule={
                            ruleIndexesForLayoutItem.hidden[f.id] !== undefined
                          }
                          isRuleCondition={
                            ruleIndexesForLayoutItem.conditions[f.id] !==
                            undefined
                          }
                          onHiddenByRuleClick={() =>
                            history.push(`/activities/${activity.id}/rules`, {
                              scrollCardIndex:
                                ruleIndexesForLayoutItem.hidden[f.id],
                            })
                          }
                          onRuleConditionClick={() =>
                            history.push(`/activities/${activity.id}/rules`, {
                              scrollCardIndex:
                                ruleIndexesForLayoutItem.conditions[f.id],
                            })
                          }
                        />
                      </Draggable>
                      {isRightDropZone && (
                        <VerticalDropzone margin={fieldMargin} />
                      )}
                    </ItemWrapper>
                    {(endOfLine || i === arr.length - 1) &&
                      isBottomDropZone && (
                        <HorizontalDropzone margin={fieldMargin} />
                      )}
                  </Fragment>
                );
              }

              const menuOptions = getMenuOptionsForLayoutItem(item, t);

              return (
                <Fragment key={item.id}>
                  {wasEndOfLine && isTopDropZone && (
                    <HorizontalDropzone margin={fieldMargin} />
                  )}
                  <ItemWrapper
                    data-qa="builder-field"
                    key={item.id}
                    className="FieldItemWrapper"
                    cols={2}
                    width={cols}
                    dragging={fieldDragging && fieldDragging.id === item.id}
                  >
                    {isLeftDropZone && (
                      <VerticalDropzone margin={fieldMargin} />
                    )}
                    <FieldPlaceholder />
                    <Draggable
                      disabled={!activity.id}
                      onStart={() => flushSync(() => setFieldDragging(item))}
                      onStop={() => {
                        flushSync(() => {
                          applyFieldDropzone();
                          setFieldDragging(null);
                          setFieldDropzone(null);
                        });
                      }}
                      position={fieldDragging ? null : { x: 0, y: 0 }}
                      handle=".FieldHandle"
                    >
                      <BuilderField
                        canDeleteFields
                        field={item}
                        columns={item.columns}
                        leftIconName="text-size-light"
                        handleProps={{ className: 'FieldHandle' }}
                        dragging={fieldDragging && fieldDragging.id === item.id}
                        validator={validateInternalName(i)}
                        onChangeDescription={() => {
                          updateSection(item.id, {
                            internalName: textBlockInternalNames[item.id],
                          });
                        }}
                        onConfirmDelete={() => handleDeleteSection(item.id)}
                        onClickChangeColumnWidth={handleResize}
                        menuOptions={menuOptions}
                        isDisabled={!activity.id}
                        onLabelChange={(id, updatedLabel) => {
                          setTextBlockInternalNames((prev) => {
                            return { ...prev, [id]: updatedLabel };
                          });
                        }}
                        label={textBlockInternalNames[item.id]}
                        onSelectAction={handleFieldSelectAction}
                        confirmDeleteMessage={t(
                          'This will permanently delete the text block.'
                        )}
                        isHiddenByRule={
                          ruleIndexesForLayoutItem.hidden[item.internalName] !==
                          undefined
                        }
                        onHiddenByRuleClick={() =>
                          history.push(`/activities/${activity.id}/rules`, {
                            scrollCardIndex:
                              ruleIndexesForLayoutItem.hidden[
                                item.internalName
                              ],
                          })
                        }
                      />
                    </Draggable>
                    {isRightDropZone && (
                      <VerticalDropzone margin={fieldMargin} />
                    )}
                  </ItemWrapper>
                  {(endOfLine || i === arr.length - 1) && isBottomDropZone && (
                    <HorizontalDropzone margin={fieldMargin} />
                  )}
                </Fragment>
              );
            })}
          </FieldLayout>
        </BuilderActivity>

        <ActivitiesFooter>
          <NewField
            data-qa="add-new-custom-field-button"
            onClick={handleNewCustomField}
            label={t('+ add new custom field')}
            disabled={!activity.id}
          />
          <NewField
            data-qa="add-new-activity-field-button"
            onClick={handleNewActivityField}
            label={t('+ add new activity field')}
            disabled={!activity.id}
          />
          <NewField
            onClick={() =>
              setTextBlockModal({
                internalName: '',
                label: '<p style="line-height: 1.25;"></p>',
                index: activity.layout.length,
              })
            }
            label={t('+ add new text block')}
            disabled={!activity.id}
          />
        </ActivitiesFooter>

        <ActivityFieldWizard
          context={wizardData}
          {...activityFieldWizardModalProps}
        />

        {textBlockModal && (
          <TextBlockModal
            textBlock={textBlockModal}
            onHide={onHideTextBlockModal}
            onConfirm={onConfirmTextBlockModal}
            validateInternalName={validateInternalName(textBlockModal.index)}
          />
        )}
        {showCustomFieldModal && (
          <CustomFieldChooserModal
            show
            enableReadonlyToggle
            context={wizardData}
            onHide={() => setShowCustomFieldModal(false)}
            onSave={handleCustomFieldSave}
            label={
              stagedDisplayNamesById?.[wizardData?.field?.id] ??
              wizardData?.field?.displayName
            }
            isActivityField
            notice={
              activity?.associationMode ===
              OBJECT_ASSOCATIONS.NO_OBJECT_ASSOCIATIONS
                ? t(
                    "Since this activity has no associations, this field won't pre-populate or update records"
                  )
                : null
            }
            {...customFieldChooserProps}
          />
        )}

        {showPreview && (
          <ActivityBuilderPreview
            activity={activity}
            onHide={() => {
              setShowPreview(false);
            }}
          />
        )}
      </BigCard>
    </>
  );
}

export default ActivitiesBuilder;
